/**
 * RBAC API surface — ported from `clario-frontend/src/services/rbac/rbac.api.ts`.
 *
 * Uses the envelope-unwrapping `apiService` for endpoints that return the
 * standard `{ data: T }` envelope, and the raw `baseService` axios instance
 * for endpoints that need the surrounding pagination metadata.
 */

import { apiService, baseService } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';
import { API_ENDPOINTS } from '@clarix/utils';
import type {
  PermissionTemplate,
  CreateTemplatePayload,
  UpdateTemplatePayload,
  UserPermission,
  UserPermissionListItem,
  AssignUserTemplatePayload,
  UpdateUserOverridesPayload,
  BulkAssignTemplatePayload,
  ProjectPermission,
  SetProjectPermissionPayload,
  TempElevation,
  GrantTempElevationPayload,
  AuditLogEntry,
  AuditLogFilters,
  AuditLogResponse,
  PermissionMatrix,
  MyPermissionsResponse,
} from './rbac.types';

/**
 * The shell rail and Projects both ask for this (large) map on every page load. This package
 * is an MF singleton, so one request per user + workspace is shared for a short window;
 * a live `permission_changed` refresh passes `fresh` and replaces the shared copy.
 */
const MY_PERMISSIONS_SHARE_MS = 30_000;
let sharedMyPermissions: {
  owner: string;
  at: number;
  promise: Promise<MyPermissionsResponse>;
} | null = null;

function fetchMyPermissions(fresh: boolean): Promise<MyPermissionsResponse> {
  const { user, workspaceSlug } = useAuthStore.getState();
  const owner = `${user?.sub ?? ''}|${workspaceSlug ?? user?.workspace_slug ?? ''}`;
  const shared = sharedMyPermissions;
  if (!fresh && shared && shared.owner === owner && Date.now() - shared.at < MY_PERMISSIONS_SHARE_MS) {
    return shared.promise;
  }
  const promise = apiService
    .get<MyPermissionsResponse>(API_ENDPOINTS.RBAC_MY_PERMISSIONS, {
      params: { include_projects: 'true' },
    })
    .catch((err: unknown) => {
      if (sharedMyPermissions?.promise === promise) sharedMyPermissions = null;
      throw err;
    });
  sharedMyPermissions = { owner, at: Date.now(), promise };
  return promise;
}

export const rbacApi = {
  // ─── Self Permissions ─────────────────────────────────────────
  /** Fetch org-level + all project permissions in a single call. */
  getMyPermissions: (opts: { fresh?: boolean } = {}) => fetchMyPermissions(opts.fresh === true),

  // ─── Templates ──────────────────────────────────────────────────

  listTemplates: (params?: {
    active?: boolean;
    level_id?: number;
    department_id?: number;
    track?: string;
    project_id?: string;
    scope?: 'new_project';
  }) =>
    baseService
      .get<{ data: PermissionTemplate[] }>(API_ENDPOINTS.RBAC_TEMPLATES, { params })
      .then((r) => r.data.data),

  getTemplate: (id: string) =>
    apiService.get<PermissionTemplate>(`${API_ENDPOINTS.RBAC_TEMPLATES}/${id}`),

  createTemplate: (data: CreateTemplatePayload) =>
    apiService.post<PermissionTemplate>(API_ENDPOINTS.RBAC_TEMPLATES, data),

  updateTemplate: (id: string, data: UpdateTemplatePayload) =>
    apiService.put<PermissionTemplate>(`${API_ENDPOINTS.RBAC_TEMPLATES}/${id}`, data),

  deleteTemplate: (id: string) => apiService.delete<void>(`${API_ENDPOINTS.RBAC_TEMPLATES}/${id}`),

  // ─── User Permissions ───────────────────────────────────────────

  getUserPermission: (userId: string) =>
    apiService.get<UserPermission>(`${API_ENDPOINTS.RBAC_USERS}/${userId}/permissions`),

  assignUserTemplate: (userId: string, data: AssignUserTemplatePayload) =>
    apiService.put<UserPermission>(`${API_ENDPOINTS.RBAC_USERS}/${userId}/permissions`, data),

  updateUserOverrides: (userId: string, data: UpdateUserOverridesPayload) =>
    apiService.put<UserPermission>(
      `${API_ENDPOINTS.RBAC_USERS}/${userId}/permissions/overrides`,
      data,
    ),

  listAllUserPermissions: (params?: { search?: string; template_ids?: string }) =>
    baseService
      .get<{ data: UserPermissionListItem[] }>(`${API_ENDPOINTS.RBAC_USERS}/permissions`, {
        params,
      })
      .then((r) => r.data.data),

  deleteUserPermission: (userId: string) =>
    apiService.delete<void>(`${API_ENDPOINTS.RBAC_USERS}/${userId}/permissions`),

  bulkAssignTemplate: (data: BulkAssignTemplatePayload) =>
    apiService.post<void>(`${API_ENDPOINTS.RBAC_USERS}/permissions/bulk-assign`, data),

  // ─── Permission Matrix ──────────────────────────────────────────

  getPermissionMatrix: (userId: string, projectId?: string) =>
    apiService.get<PermissionMatrix>(`${API_ENDPOINTS.RBAC_USERS}/${userId}/matrix`, {
      params: projectId ? { project_id: projectId } : undefined,
    }),

  // ─── Project Permissions ────────────────────────────────────────

  listProjectPermissions: (projectId: string) =>
    apiService.get<ProjectPermission[]>(`${API_ENDPOINTS.RBAC_PROJECTS}/${projectId}/permissions`),

  setProjectPermission: (projectId: string, data: SetProjectPermissionPayload) =>
    apiService.post<ProjectPermission>(
      `${API_ENDPOINTS.RBAC_PROJECTS}/${projectId}/permissions`,
      data,
    ),

  updateProjectOverrides: (projectId: string, userId: string, overrides: Record<string, boolean>) =>
    apiService.put<ProjectPermission>(
      `${API_ENDPOINTS.RBAC_PROJECTS}/${projectId}/permissions/${userId}/overrides`,
      { overrides },
    ),

  removeProjectPermission: (projectId: string, userId: string) =>
    apiService.delete<void>(`${API_ENDPOINTS.RBAC_PROJECTS}/${projectId}/permissions/${userId}`),

  // ─── Temp Elevations ────────────────────────────────────────────

  listTempElevations: (params?: { user_id?: string; project_id?: string; status?: string }) =>
    apiService.get<TempElevation[]>(API_ENDPOINTS.RBAC_ELEVATIONS, { params }),

  grantTempElevation: (data: GrantTempElevationPayload) =>
    apiService.post<TempElevation>(API_ENDPOINTS.RBAC_ELEVATIONS, data),

  revokeTempElevation: (id: string) =>
    apiService.post<TempElevation>(`${API_ENDPOINTS.RBAC_ELEVATIONS}/${id}/revoke`),

  // ─── Audit Logs ─────────────────────────────────────────────────

  listAuditLogs: (params?: AuditLogFilters) =>
    baseService
      .get<{
        data: AuditLogEntry[];
        pagination: AuditLogResponse['pagination'];
      }>(API_ENDPOINTS.RBAC_AUDIT_LOGS, { params })
      .then((res) => res.data),
};

export type RbacApi = typeof rbacApi;
